import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { LoaderCircle } from 'lucide-react';

import { ApiError } from '@/api/client';
import { useSubmitPaymentClaim } from '@/api/hooks/usePayments';
import type { PaymentMethod } from '@/api/payments';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from '@/components/ui/use-toast';

const METHODS: PaymentMethod[] = ['Upi', 'Bank', 'Cash', 'Other'];

export function SubmitClaimDialog({
  open,
  onOpenChange,
  suggestedAmount,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  suggestedAmount: number;
}) {
  const submitClaim = useSubmitPaymentClaim();

  const [amount, setAmount] = useState('');
  const [method, setMethod] = useState<PaymentMethod>('Upi');
  const [transactionRef, setTransactionRef] = useState('');
  const [noReference, setNoReference] = useState(false);
  const [months, setMonths] = useState('1');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setAmount(suggestedAmount > 0 ? String(suggestedAmount) : '');
      setMethod('Upi');
      setTransactionRef('');
      setNoReference(false);
      setMonths('1');
      setNotes('');
      setError(null);
    }
  }, [open, suggestedAmount]);

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);

    if (!Number(amount) || Number(amount) <= 0) {
      setError('Enter the amount you paid.');
      return;
    }
    if (!noReference && !transactionRef.trim()) {
      setError('Provide a transaction reference (UTR), or tick "I paid, no UTR".');
      return;
    }

    try {
      await submitClaim.mutateAsync({
        amount: Number(amount),
        method,
        transactionRef: noReference ? undefined : transactionRef.trim(),
        noReference,
        monthsRequested: Math.max(1, Number(months) || 1),
        notes: notes.trim() || undefined,
      });
      toast({
        title: 'Payment claim submitted',
        description: "We'll verify and activate your subscription within 24 hours.",
        variant: 'success',
      });
      onOpenChange(false);
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.code === 'DUPLICATE_CLAIM' ? err.message : err.message);
      } else {
        setError('Something went wrong. Please try again.');
      }
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Submit payment claim</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4" noValidate>
          <div className="space-y-1.5">
            <Label htmlFor="claim-amount">Amount paid (₹) *</Label>
            <Input
              id="claim-amount"
              type="number"
              min={1}
              step="0.01"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Payment method</Label>
              <Select value={method} onValueChange={(v) => setMethod(v as PaymentMethod)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {METHODS.map((m) => (
                    <SelectItem key={m} value={m}>
                      {m}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="claim-months">Months</Label>
              <Input id="claim-months" type="number" min={1} max={60} value={months} onChange={(e) => setMonths(e.target.value)} />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="claim-utr">Transaction reference (UTR)</Label>
            <Input
              id="claim-utr"
              value={transactionRef}
              disabled={noReference}
              onChange={(e) => setTransactionRef(e.target.value)}
              placeholder="From your UPI app or bank statement"
            />
            <label className="flex items-center gap-2 text-xs text-muted-foreground">
              <input
                type="checkbox"
                checked={noReference}
                onChange={(e) => setNoReference(e.target.checked)}
                className="h-3.5 w-3.5 accent-primary"
              />
              I paid, but don't have a UTR
            </label>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="claim-notes">Notes</Label>
            <Input id="claim-notes" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Optional" />
          </div>

          {error && (
            <p role="alert" className="rounded-md border border-status-expired/30 bg-status-expired/10 px-3 py-2 text-sm text-status-expired">
              {error}
            </p>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={submitClaim.isPending}>
              {submitClaim.isPending && <LoaderCircle className="animate-spin" />}
              Submit claim
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
